import { deleteObject, getDownloadURL, getStorage, listAll, putFile, ref } from '@react-native-firebase/storage';
import {
  deleteObject as deleteObjectWeb,
  FirebaseStorage,
  getDownloadURL as getDownloadURLWeb,
  listAll as listAllWeb,
  ref as storageRef,
  uploadBytesResumable as uploadBytesResumableWeb
} from 'firebase/storage';
import { Platform } from 'react-native';

import { storage } from '@/firebaseConfig';

import { FileToUpload, UploadResult } from './uploadMultipleToFireStorage';

interface UploadToFireStorageProps {
  path: string;
  file: FileToUpload;
  onProgress?: (progress: number) => void;
}

const uploadToFireStorageMobile = async ({
  path,
  file,
  onProgress
}: UploadToFireStorageProps): Promise<UploadResult> => {
  const imageRef = ref(getStorage(), `${path}${file.name}`);
  const deleteRef = ref(getStorage(), path);

  // Clear the path from all files
  await listAll(deleteRef).then(async (listResults) => {
    const promises = listResults.items.map((item) => {
      return deleteObject(item);
    });
    await Promise.all(promises);
  });

  const uploadTask = putFile(imageRef, file.uri);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress && onProgress(progress);
      },
      (error) => {
        console.error(error);
        reject(error);
      },
      async () => {
        if (uploadTask.snapshot !== null) {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          resolve({
            downloadUrl,
            metadata: uploadTask.snapshot.metadata
          });
        }
      }
    );
  });
};

const uploadToFireStorageWeb = async ({ path, file, onProgress }: UploadToFireStorageProps): Promise<UploadResult> => {
  const fetchResponse = await fetch(file.uri);
  const theBlob = await fetchResponse.blob();

  const imageRef = storageRef(storage() as FirebaseStorage, `${path}${file.name}`);
  const deleteRef = storageRef(storage() as FirebaseStorage, path);

  // Clear the path from all files
  listAllWeb(deleteRef).then(async (listResults) => {
    const promises = listResults.items.map((item) => {
      return deleteObjectWeb(item);
    });
    await Promise.all(promises);
  });

  const uploadTask = uploadBytesResumableWeb(imageRef, theBlob);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress && onProgress(progress);
      },
      (error) => {
        console.error(error);
        reject(error);
      },
      async () => {
        if (uploadTask.snapshot !== null) {
          const downloadUrl = await getDownloadURLWeb(uploadTask.snapshot.ref);
          resolve({
            downloadUrl,
            metadata: uploadTask.snapshot.metadata
          });
        }
      }
    );
  });
};

/**
 * Util function to upload files to firebase storage
 * @param path - Path of storage where to save the file and delete all the old files
 * @param fileToUpload - The file to upload of FileToUpload type
 * @param onProgress - Optional callback function with progress value
 * @returns Promise with downloadUrl and metadata object
 */
export const uploadToFireStorage = (
  path: string,
  file: FileToUpload,
  onProgress?: (progress: number) => void
): Promise<UploadResult> => {
  if (Platform.OS === 'web') {
    return uploadToFireStorageWeb({ path, file, onProgress });
  }
  return uploadToFireStorageMobile({ path, file, onProgress });
};
